import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  LinearProgress,
  Tooltip,
  Button,
} from "@mui/material";
import { EventAvailable, ErrorOutline, AccessTime } from "@mui/icons-material";
import { CustomFooterPersonalized } from "../utils/CustomFooterPersonalized";
import "../style/style.css";
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  itIT,
} from "../utils/PersistedDataGridPro";
import unsplash from "../api/unsplash";
import moment from "moment";
function inspectionStatus(value) {
  if (!value || moment(value).isBefore(moment(), "day")) return "error";
  return moment(value).diff(moment().startOf("day"), "days") <= 60
    ? "warning"
    : "success";
}
const statusFilters = [
  { value: "error", label: "Scaduti o da effettuare", icon: <ErrorOutline /> },
  { value: "warning", label: "Entro 60 giorni", icon: <AccessTime /> },
  { value: "success", label: "Validi", icon: <EventAvailable /> },
];
function Toolbar({ soloMiei, onToggleSoloMiei, stati, onToggleStato }) {
  return (
    <GridToolbarContainer
      sx={{ px: 1, py: 1.5, gap: 1.5, alignItems: "center", flexWrap: "wrap" }}
    >
      <GridToolbarQuickFilter
        placeholder="Cerca..."
        sx={{ width: { xs: "100%", sm: 320 } }}
      />
      <Button
        variant={soloMiei ? "contained" : "outlined"}
        aria-pressed={soloMiei}
        onClick={onToggleSoloMiei}
        sx={{
          ...(soloMiei && { "&&": { color: "#fff !important" } }),
        }}
      >
        Solo Miei
      </Button>
      {statusFilters.map((status) => (
        <Tooltip key={status.value} title={status.label}>
          <Chip
            icon={status.icon}
            label={status.label}
            color={status.value}
            variant={stati.includes(status.value) ? "filled" : "outlined"}
            onClick={() => onToggleStato(status.value)}
            aria-pressed={stati.includes(status.value)}
            sx={{
              ...(stati.includes(status.value) && {
                "&&": { color: "#fff !important" },
              }),
            }}
          />
        </Tooltip>
      ))}
    </GridToolbarContainer>
  );
}
const columns = [
  {
    field: "Scadenza",
    headerName: "Scadenza",
    type: "date",
    width: 170,
    valueGetter: ({ value }) => (value ? moment(value).toDate() : null),
    headerAlign: "center",
    align: "center",
    renderCell: ({ value }) => {
      const status = inspectionStatus(value);
      const overdue = status === "error";
      const soon = status === "warning";
      return (
        <Tooltip
          title={
            !value
              ? "Nessun sopralluogo registrato"
              : overdue
                ? "Sopralluogo scaduto"
                : soon
                  ? "In scadenza entro 60 giorni"
                  : "Sopralluogo in corso di validità"
          }
        >
          <Chip
            size="small"
            icon={
              overdue ? (
                <ErrorOutline />
              ) : soon ? (
                <AccessTime />
              ) : (
                <EventAvailable />
              )
            }
            label={value ? moment(value).format("DD/MM/YYYY") : "Da effettuare"}
            color={overdue ? "error" : soon ? "warning" : "success"}
            variant="outlined"
            sx={{ fontWeight: 500, fontSize: 12 }}
          />
        </Tooltip>
      );
    },
  },
  { field: "Cliente", headerName: "Cliente", minWidth: 240, flex: 1.5 },
  { field: "Sede", headerName: "Sede", minWidth: 170, flex: 1 },
  { field: "Indirizzo", headerName: "Indirizzo", minWidth: 240, flex: 1.5 },
  { field: "Medico", headerName: "Medico", minWidth: 200, flex: 1 },
  { field: "Categoria", headerName: "Categoria", minWidth: 150, flex: 1 },
  { field: "Utenza", headerName: "Utenza", minWidth: 170, flex: 1 },
];
export default function ScadenzarioSopralluoghi() {
  const [soloMiei, setSoloMiei] = useState(false);
  const [stati, setStati] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setRows([]);
    unsplash
      .get("scadenzarioSopralluoghi", {
        params: { soloMiei: soloMiei ? "1" : "0" },
        headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` },
      })
      .then(({ data }) => {
        if (active) setRows(data.data);
      })
      .catch(() => {
        if (active)
          setError("Impossibile caricare lo scadenzario sopralluoghi.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [soloMiei]);
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h5" sx={{ mb: 1, color: "#11526F" }}>
          Scadenzario sopralluoghi
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ultima scadenza per sede · Rosso: scaduto o da effettuare · Arancione:
          entro 60 giorni · Verde: valido
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box
          sx={{ height: { xs: "70vh", md: "65vh", xl: "75vh" }, width: "100%" }}
        >
          <DataGridPro
            rows={
              stati.length
                ? rows.filter((row) =>
                    stati.includes(inspectionStatus(row.Scadenza)),
                  )
                : rows
            }
            columns={columns.map((column) => ({
              headerAlign: "center",
              align: "center",
              ...column,
            }))}
            getRowId={(row) => row.ID}
            loading={loading}
            components={{
              Toolbar,
              Footer: CustomFooterPersonalized,
              LoadingOverlay: LinearProgress,
            }}
            componentsProps={{
              toolbar: {
                stati,
                onToggleStato: (stato) =>
                  setStati((selected) =>
                    selected.includes(stato)
                      ? selected.filter((value) => value !== stato)
                      : [...selected, stato],
                  ),
                soloMiei,
                onToggleSoloMiei: () => setSoloMiei((value) => !value),
              },
              footer: {
                csvOptions: {
                  fileName: "Scadenzario sopralluoghi",
                  delimiter: ";",
                  utf8WithBom: true,
                },
              },
            }}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            initialState={{
              pinnedColumns: { left: ["Scadenza"] },
              sorting: { sortModel: [{ field: "Scadenza", sort: "asc" }] },
            }}
            getRowHeight={() => "auto"}
            disableSelectionOnClick
            sx={{
              fontSize: 12,
              "& .MuiDataGrid-cell": { whiteSpace: "normal", py: 1.5 },
              "& .MuiDataGrid-cellContent": { textTransform: "none!important" },
              "& .MuiDataGrid-row:hover": { backgroundColor: "#eef2f6" },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
